import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from '@mui/material/styles';
import { api } from 'common';
import CircularLoading from "../components/CircularLoading";
import { colors } from '../components/Theme/WebTheme';
import { SECONDORY_COLOR, labelText } from "../common/sharedFunctions";
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { getModuleActionPermissions, filterEditableActions, showActionPermissionDenied } from "common/src/actions/userPermissions";

const shipmentTypeLookup = {
  "": "None / Other Vehicle Type",
  "Domestic Shipment": "Domestic Shipment",
  "Import Shipment": "Import Shipment",
  "Export Shipment": "Export Shipment"
};

const slotDirectionLookup = {
  none: "none",
  add: "add",
  deduct: "deduct"
};

const getVehicleSizeFromName = (value) => {
  const name = (value || "").toString().toLowerCase();
  if (/(^|[^0-9])10\s*(ft|footer|feet)?([^0-9]|$)/.test(name)) return "10ft";
  if (/(^|[^0-9])20\s*(ft|footer|feet)?([^0-9]|$)/.test(name)) return "20ft";
  if (/(^|[^0-9])40\s*(ft|footer|feet)?([^0-9]|$)/.test(name)) return "40ft";
  return "";
};

export default function WorkflowConfiguration() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const label = (key, fallback) => labelText(t, key, fallback);
  const {
    fetchCarTypes,
    fetchWorkflowConfigurations,
    addWorkflowConfiguration,
    updateWorkflowConfiguration,
    deleteWorkflowConfiguration
  } = api;
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);

    const workflowConfigurationPermissions = getModuleActionPermissions({
        role: auth && auth.profile && auth.profile.usertype,
        moduleKey: "workflowConfiguration",
        accessPermissions: settings && settings.accessPermissions
    });
  const cartypes = useSelector(state => state.cartypes);
  const workflowconfigs = useSelector(state => state.workflowconfigs);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    dispatch(fetchCarTypes());
    dispatch(fetchWorkflowConfigurations());
  }, [dispatch, fetchCarTypes, fetchWorkflowConfigurations]);

  const vehicleTypeLookup = useMemo(() => {
    const lookup = {};
    (cartypes.cars || []).forEach((car) => {
      if (car && car.id) {
        lookup[car.id] = car.name || car.id;
      }
    });
    return lookup;
  }, [cartypes.cars]);

  const prepareConfig = (rowData = {}) => {
    const car = (cartypes.cars || []).find((item) => item.id === rowData.vehicleTypeId);
    const vehicleTypeName = car ? car.name : (rowData.vehicleTypeName || vehicleTypeLookup[rowData.vehicleTypeId] || "");
    const slotRules = rowData.workflowsSlotRules === true || rowData.slotRuleEnabled === true;
    return {
      ...rowData,
      vehicleTypeName,
      vehicleSize: rowData.vehicleSize || getVehicleSizeFromName(vehicleTypeName),
      formsPrimary: rowData.formsPrimary !== false,
      active: rowData.active !== false,
      workflowsSlotRules: slotRules,
      slotRuleEnabled: slotRules,
      slotRuleDirection: slotRules ? (rowData.slotRuleDirection || "none") : "none",
      slotRuleAmount: slotRules ? (rowData.slotRuleAmount || 0) : 0,
      workflowsBookingReceipt: rowData.workflowsBookingReceipt !== false,
      emailBookingCompletedReceipt: rowData.emailBookingCompletedReceipt !== false
    };
  };

  const columns = [
    {
      title: label("vehicle_type", "Vehicle Type"),
      field: "vehicleTypeId",
      lookup: vehicleTypeLookup,
      render: rowData => rowData.vehicleTypeName || vehicleTypeLookup[rowData.vehicleTypeId] || "",
      customSort: (a, b) => (a.vehicleTypeName || "").localeCompare(b.vehicleTypeName || "")
    },
    { title: label("vehicleSize", "Vehicle Size"), field: "vehicleSize", initialEditValue: "" },
    {
      title: label("shipmentType", "Shipment Type"),
      field: "shipmentType",
      lookup: shipmentTypeLookup,
      initialEditValue: "Domestic Shipment",
      customSort: (a, b) => (a.shipmentType || "").localeCompare(b.shipmentType || "")
    },
    { title: label("primary_booking_information_form", "Primary Form"), field: "formsPrimary", type: "boolean", initialEditValue: true },
    { title: label("domestic_booking_information_form", "Domestic Form"), field: "formsDomestic", type: "boolean", initialEditValue: false },
    { title: label("import_booking_information_form", "Import Form"), field: "formsImport", type: "boolean", initialEditValue: false },
    { title: label("export_booking_information_form", "Export Form"), field: "formsExport", type: "boolean", initialEditValue: false },
    { title: label("empty_container_return", "Empty Return"), field: "workflowsEmptyReturn", type: "boolean", initialEditValue: false },
    { title: label("empty_pickup_container", "Empty Pick Up"), field: "workflowsEmptyPickup", type: "boolean", initialEditValue: false },
    { title: label("slot_rules", "Slot Rules"), field: "workflowsSlotRules", type: "boolean", initialEditValue: false },
    { title: label("slot_direction", "Slot Direction"), field: "slotRuleDirection", lookup: slotDirectionLookup, initialEditValue: "none" },
    { title: label("slot_amount", "Slot Amount"), field: "slotRuleAmount", type: "numeric", initialEditValue: 0 },
    { title: label("booking_receipt", "Booking Receipt"), field: "workflowsBookingReceipt", type: "boolean", initialEditValue: true },
    { title: label("active_status", "Active"), field: "active", type: "boolean", initialEditValue: true }
  ];

  const loading = workflowconfigs.loading || cartypes.loading;

  return (
    loading ? <CircularLoading /> :
      <ThemeProvider theme={theme}>
        <MaterialTable
          title={label("workflow_configuration", "Workflow Configuration")}
          columns={columns}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            padding: "20px",
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          }}
          data={workflowconfigs.configs || []}
          onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
          options={{
            exportButton: true,
            search: true,
            sorting: true,
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE
            }),
            ...TableStyle()
          }}
          localization={localization(t)}
          editable={settings && settings.AllowCriticalEditsAdmin ? filterEditableActions({ editable: {
            onRowAdd: newData =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  dispatch(addWorkflowConfiguration(prepareConfig(newData))).then(resolve).catch((error) => {
                    alert(error && error.message ? error.message : label('no_details_error', 'Please fill up all the details properly.'));
                    reject();
                  });
                }, 600);
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  if (newData !== oldData) {
                    dispatch(updateWorkflowConfiguration(prepareConfig(newData))).then(resolve).catch((error) => {
                      alert(error && error.message ? error.message : label('no_details_error', 'Please fill up all the details properly.'));
                      reject();
                    });
                  } else {
                    resolve();
                  }
                }, 600);
              }),
            onRowDelete: oldData =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  dispatch(deleteWorkflowConfiguration(oldData)).then(resolve).catch((error) => {
                    alert(error && error.message ? error.message : label('no_details_error', 'Please fill up all the details properly.'));
                    reject();
                  });
                }, 600);
              }),
          }, permissions: workflowConfigurationPermissions }) : null}
        />
      </ThemeProvider>
  );
}
